import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdminUsersList } from '../../../hooks/useAdminUsersList';
import { useAuthStore } from '../../../stores/authStore';
import { useRole } from '../../../hooks/useRole';
import { CardNB, ButtonNB, BadgeNB } from '../../../components/ui-nb';

type FilterTab = 'all' | 'technicians' | 'cashiers';
type UserRole = 'technician' | 'cashier';

const ROLE_LABELS: Record<UserRole, string> = {
  technician: 'Technician',
  cashier: 'Cashier',
};

const PAGE_SIZE = 20;

export default function UserList() {
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const organizationId = useAuthStore(s => s.user?.organizationId);
  const userRole = useRole();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { users, totalCount, isLoading, error, toggleStatus } = useAdminUsersList({
    organizationId: organizationId ?? null,
    search: debouncedSearch || null,
    page,
  });

  // Apply role filter client-side
  const filteredUsers = users.filter((user) => {
    if (filter === 'technicians') return user.role === 'technician';
    if (filter === 'cashiers') return user.role === 'cashier';
    return true;
  });

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const currentPage = page + 1;

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'technicians', label: 'Technicians' },
    { key: 'cashiers', label: 'Cashiers' },
  ];

  const handleToggle = async (userId: string, current: boolean) => {
    await toggleStatus(userId, current);
  };

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-nb-black">
          Users
        </h1>
        <Link to="/en/admin/users/invite">
          <ButtonNB variant="primary" size="sm">
            Invite User
          </ButtonNB>
        </Link>
      </div>

      {error && (
        <div className="bg-nb-red/10 text-nb-red p-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <CardNB className="p-0 overflow-x-auto">
        <div className="border-b-3 border-nb-black">
          <nav className="flex gap-6 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`py-4 text-sm font-nb font-medium border-b-2 transition-colors ${
                  filter === tab.key
                    ? 'border-nb-black text-nb-black'
                    : 'border-transparent text-nb-text-muted hover:text-nb-black'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Search bar */}
        <div className="p-4 border-b-3 border-nb-black">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-nb w-full max-w-md p-2 border-2 border-nb-black bg-nb-white text-nb-black text-sm outline-none"
            style={{ borderRadius: 0 }}
          />
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-nb-cream">
              <th className="text-left p-3 text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide">User</th>
              <th className="text-left p-3 text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide">Role</th>
              <th className="text-left p-3 text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide">Branch(es)</th>
              <th className="text-left p-3 text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide">Status</th>
              <th className="text-right p-3 text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="border-t-3 border-nb-black">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-b border-nb-black/10">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-nb-black text-nb-white flex items-center justify-center font-nb font-medium text-sm">
                      {user.full_name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-nb font-medium text-nb-black">{user.full_name}</div>
                      <div className="text-xs text-nb-text-muted">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-nb-text-secondary">{ROLE_LABELS[user.role as UserRole]}</span>
                </td>
                <td className="p-4">
                  {user.branches.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {user.branches.map((branch) => (
                        <span key={branch} className="text-xs bg-nb-cream text-nb-text-secondary px-2 py-0.5">
                          {branch}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-nb-text-muted">—</span>
                  )}
                </td>
                <td className="p-4">
                  <BadgeNB variant={user.is_active ? 'success' : 'warning'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </BadgeNB>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-3">
                    <Link
                      to={`/en/admin/users/${user.id}`}
                      className="text-nb-black text-sm font-nb font-medium"
                    >
                      View
                    </Link>
                    {userRole === 'admin' && (
                      <button
                        onClick={() => handleToggle(user.id, user.is_active)}
                        className="text-nb-text-muted text-sm font-nb font-medium bg-none border-none cursor-pointer"
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t-3 border-nb-black">
            <div className="text-sm text-nb-text-muted">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <ButtonNB
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </ButtonNB>
              <ButtonNB
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </ButtonNB>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12 text-nb-text-muted text-sm">Loading...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-nb-text-muted text-sm">No users found.</div>
        ) : null}
      </CardNB>
    </div>
  );
}